import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { Action } from '@interstate/components/Action';
import { BreakdownButtonContainer } from './BreakdownActionButton.style';
import { useDispatch, useSelector } from 'react-redux';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as baseDealAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/baseDealAnalyticsActionCreators';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as dealModeSelectors from '@makemydeal/dr-dash-store/selectors/dealModeSelectors';

import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { ChevronLeftIcon, ChevronRightIcon } from '@interstate/components/Icons';
import { Typography } from '@interstate/components/Typography';

type BreakdownActionButtonProps = {
    mode?: 'slideout' | 'application';
};

export const BreakdownActionButton: FC<BreakdownActionButtonProps> = ({ mode = 'application' }) => {
    const dispatch = useDispatch();

    const applicationBreakdownShown = useSelector(baseDealSelectors.getBreakdownStatus);
    const slideoutBreakdownShown = useSelector(baseDealSelectors.getBreakdownSlideoutStatus);

    const setApplicationBreakdownStatus = baseDealActionCreators.setBreakdownStatus;
    const setSlideoutBreakdownStatus = baseDealActionCreators.setBreakdownSlideoutStatus;

    const showBreakdown = mode === 'slideout' ? slideoutBreakdownShown : applicationBreakdownShown;
    const setBreakdownStatus = mode === 'slideout' ? setSlideoutBreakdownStatus : setApplicationBreakdownStatus;

    const isDealCentralExperience = useSelector(dealerSelectors.isDealCentralExperience);
    const isFnIDealMode = useSelector(dealModeSelectors.isFnIDealMode);
    const prevIsFnIDealMode = useRef(isFnIDealMode);

    const handleBreakdownStatusAndAnalytics = () => {
        const { breakdownExpand, breakdownCollapse } = baseDealAnalyticsActionCreators;
        const shouldShowBreakdown = !showBreakdown;
        dispatch(setBreakdownStatus(shouldShowBreakdown, 'click'));
        if (shouldShowBreakdown) {
            dispatch(breakdownExpand());
        } else {
            dispatch(breakdownCollapse());
        }
    };

    useEffect(() => {
        // For FNI mode - Don't Show breakdown by default
        // For Desking mode - Show breakdown by default
        // istanbul ignore next
        if (prevIsFnIDealMode.current !== isFnIDealMode && isDealCentralExperience) {
            prevIsFnIDealMode.current = isFnIDealMode;
            // If the mode has changed, reset the breakdown status
            dispatch(baseDealActionCreators.setBreakdownStatus(!isFnIDealMode));
        }
    }, [isFnIDealMode]);

    return (
        <BreakdownButtonContainer>
            <Action data-testid="base-deal-breakdown" onClick={handleBreakdownStatusAndAnalytics}>
                <Typography variant="anchor-block-md" color="base.color.blue.500" className="base-deal-breakdown-icon">
                    {showBreakdown ? null : <ChevronLeftIcon />}
                    Breakdown
                    {showBreakdown ? <ChevronRightIcon /> : null}
                </Typography>
            </Action>
        </BreakdownButtonContainer>
    );
};
